import { Contract, ObjectEntity, ObjectEntityItem, PageDto } from '@ekt-group/general-purpose-api-interfaces';
import { ObjectsSideloading } from '@ekt-group/general-purpose-api-interfaces/src/modules/objects/types/objects-sideloading.enum';
import { CustomerContractRole } from '@ekt-group/general-purpose-api-interfaces/src/types/customer-contract-role.enum';
import { ObjectItemSideloading } from '@ekt-group/general-purpose-api-interfaces/src/types/object-items.enum';
import { ChevronDown, ChevronRight, Hash02, Map01, Plus, Settings04, Trash04 } from '@untitled-ui/icons-react';
import { Badge, Checkbox, Pagination, Skeleton } from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconContainer3 } from '@/components/icons';
import NoData from '@/components/NoData';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import { DATE_FORMAT } from '@/constants';
import { useUser } from '@/features/auth';
import { useGetContracts } from '@/features/contracts/api/use-get-contracts';
import DownloadDischargeSchedulesPDFButton from '@/features/contracts/components/DownloadDischargeSchedulesPDFButton';
import DownloadSchedulesExcelButton from '@/features/contracts/components/DownloadSchedulesExcelButton';
import { useGetFilters } from '@/features/desktop/api/use-get-filters';
import AdditionalDischargesActionCard from '@/features/desktop/components/AdditionalDischargesActionCard';
import ContractsActionCard from '@/features/desktop/components/ContractsActionCard';
import InvoicesActionCard from '@/features/desktop/components/InvoicesActionCard';
import ObjectsMap from '@/features/desktop/components/ObjectsMap/ObjectsMap';
import ServicesHistoryActionCard from '@/features/desktop/components/ServicesHistoryActionCard';
import { useGetObjectItems } from '@/features/objectItems/api/use-get-object-items';
import ObjectItemDischargesModal from '@/features/objectItems/components/WasteCalendar/ObjectItemDischargesModal';
import { useGetObjects } from '@/features/objects/api/use-get-objects';
import ObjectServicesModal from '@/features/objects/components/ObjectServicesModal';
import { getAllowedContractIdsFromPermissions } from '@/helpers/get-allowed-contract-ids-from-permissions';
import { parseDischargeFrequencyText } from '@/helpers/parse-discharge-frequency-text';
import { safeNumber } from '@/helpers/safe-number';
import { useBreakpoint } from '@/hooks/use-breakpoints';
import { useCollapsableList } from '@/hooks/use-collapsable-list';
import { useFilterParams } from '@/hooks/use-filter-params';
import { Locale } from '@/hooks/use-locale';
import { useUserInfoStore } from '@/store';
import { cn } from '@/utils/cn';

import { useGetObjectItemsByObjectId } from '../hooks/use-get-object-items-by-object-id';

const DesktopPage = () => {
  const { t, i18n } = useTranslation('desktopPage');

  const { data: currentUser } = useUser();
  const { selectedAccount } = useUserInfoStore();

  const [selectedObjectIdForServicesModal, setSelectedObjectIdForServicesModal] = useState<number>();
  const [objectItemDischargesModalProps, setObjectItemDischargesModalProps] = useState<{
    objectItemId: number;
    garbageId: number;
    objectAddressId: number;
  }>();
  const [isServicesModalOpen, setIsServicesModalOpen] = useState(false);
  const [isAdditionalDischargeModalOpen, setIsAdditionalDischargeModalOpen] = useState(false);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);

  const { filters, setFilterValue, clearAllFilters, hasFilters } = useFilterParams([
    'contractNumber',
    'garbageId',
    'itemTypeId',
    'objectAddressId',
    'showEndedContainers',
    'showEndedObjects',
  ]);

  const { isBelowMd } = useBreakpoint('md');

  const mapComponent = (
    <div className="h-[362px] md:h-full md:w-[562px] rounded-[32px] overflow-hidden">
      <ObjectsMap />
    </div>
  );

  const { pagination, expandedItemIds, toggleExpandItems, toggleExpandItem } = useCollapsableList<ObjectEntity>();
  const { page, pageSize, handlePaginationChange } = pagination;

  const toggleFiltersVisibility = () => {
    setIsFiltersVisible(!isFiltersVisible);
  };

  const contractsQuery = useGetContracts({
    params: {
      disablePagination: true,
      ownerCodes: [selectedAccount?.code],
    },
    queryConfig: {
      enabled: !!selectedAccount?.code,
    },
  });
  const contracts = contractsQuery.data as Contract[];

  const objectsQuery = useGetObjects({
    params: {
      contractIds:
        currentUser?.roles?.includes(CustomerContractRole.ChildContract) ||
        currentUser?.roles?.includes(CustomerContractRole.SharedObjectContract)
          ? getAllowedContractIdsFromPermissions(selectedAccount?.permissions)
          : undefined,
      contractNumbers: [filters.contractNumber],
      sideloading: [ObjectsSideloading.Contract, ObjectsSideloading.ObjectAddress, ObjectsSideloading.IsActive],
      garbageIds: [safeNumber(filters.garbageId)],
      itemTypeIds: [safeNumber(filters.itemTypeId)],
      page,
      pageSize,
      ownerCodes: [selectedAccount?.code],
      objectAddressIds: [safeNumber(filters.objectAddressId)],
      isActive: filters.showEndedObjects === 'true' ? undefined : true,
      isDeleted: false,
      // @ts-ignore
      order: 'DESC',
      orderBy: 'contract.endDate',
    },
    queryConfig: {
      enabled: !!selectedAccount?.code,
    },
  });
  const objectsData = objectsQuery.data as PageDto<ObjectEntity>;

  const objectItemsQuery = useGetObjectItems({
    params: {
      contractIds:
        currentUser?.roles?.includes(CustomerContractRole.ChildContract) ||
        currentUser?.roles?.includes(CustomerContractRole.SharedObjectContract)
          ? getAllowedContractIdsFromPermissions(selectedAccount?.permissions)
          : undefined,
      ownerCodes: [selectedAccount?.code],
      objectIds: objectsData?.data?.map((object) => object.id),
      disablePagination: true,
      garbageIds: [safeNumber(filters.garbageId)],
      itemTypeIds: [safeNumber(filters.itemTypeId)],
      sideloading: [
        ObjectItemSideloading.Garbage,
        ObjectItemSideloading.ItemType,
        ObjectItemSideloading.UpcomingDischargeDate,
        ObjectItemSideloading.IsActive,
      ],
      isActive: filters.showEndedContainers === 'true' ? undefined : true,
      isDeleted: false,
    },
    queryConfig: {
      enabled: Boolean(objectsData?.data?.length && selectedAccount?.code),
    },
  });

  const filtersQuery = useGetFilters({
    userId: currentUser.id,
    types: ['garbage', 'itemType', 'objects', 'addresses'],
    ownerCodes: [selectedAccount?.code],
  });

  const handleCloseServicesModal = () => {
    setIsServicesModalOpen(false);
  };

  const handleCloseAdditionalDischargesModal = () => {
    setIsAdditionalDischargeModalOpen(false);
  };

  const handleExtraServicesButtonClick = (objectId: number) => {
    setSelectedObjectIdForServicesModal(objectId);
    setIsServicesModalOpen(true);
  };

  const handleAdditionalDischargeButtonClick = (objectItemId: number, garbageId: number, objectAddressId: number) => {
    setObjectItemDischargesModalProps({
      objectItemId,
      garbageId,
      objectAddressId,
    });
    setIsAdditionalDischargeModalOpen(true);
  };

  const objectItemsByObjectId = useGetObjectItemsByObjectId(objectItemsQuery.data);

  const getObjectItemsForObject = (objectId: number): ObjectEntityItem[] => {
    return objectItemsByObjectId[objectId] || [];
  };

  const appliedFiltersCount = Object.values(filters)?.filter(Boolean).length;

  return (
    <div className="flex flex-col h-full gap-6 text-base lg:px-8 sm:px-6 px-3">
      <div className="flex flex-col md:flex-row gap-5">
        <div className="flex flex-col gap-8 flex-1">
          <div className="flex flex-col gap-4 justify-between">
            <div className="flex sm:flex-row flex-col flex-1 gap-4">
              <ContractsActionCard />
              <ServicesHistoryActionCard />
            </div>
            <div className="flex flex-1 sm:flex-row flex-col gap-4 justify-between">
              <InvoicesActionCard />
              <AdditionalDischargesActionCard />
            </div>
          </div>
          {isBelowMd && mapComponent}
          <div className="flex flex-col gap-2 bg-white p-6 rounded-[32px]">
            <div className="flex flex-col gap-3">
              <div className="flex sm:flex-row flex-col justify-between gap-3">
                <div className="flex items-center justify-between order-1 col-span-6 gap-4 sm:justify-start sm:col-span-3">
                  <span className="sm:text-[32px] text-[24px] font-semibold">{t('dashboard', { ns: 'header' })}</span>
                  <button onClick={toggleFiltersVisibility} className="flex items-center gap-1 text-[#A7AD2A] relative">
                    <Settings04 className="w-4 h-4" />
                    <span className="text-base font-medium">{t(isFiltersVisible ? 'closeFilters' : 'openFilters', { ns: 'common' })}</span>
                    {hasFilters && <Badge count={appliedFiltersCount} showZero={false} color="#A7AD2A" />}
                  </button>
                </div>
                <Button
                  variant="primaryLight"
                  onClick={() => toggleExpandItems(objectsData?.data || [])}
                  className="order-8 px-5 max-h-12 h-12 col-span-6 sm:justify-self-end sm:order-0 sm:order-2 sm:col-span-3 sm:w-auto w-full"
                >
                  {expandedItemIds.length ? t('collapseAll', { ns: 'common' }) : t('expandAll', { ns: 'common' })}
                </Button>
              </div>
            </div>
            <div className={cn('flex flex-col gap-4', { hidden: !isFiltersVisible })}>
              <div className="flex md:flex-row flex-col gap-2">
                <div className="flex gap-2 flex-1 flex-col">
                  <Select
                    name="contractNumber"
                    value={filters.contractNumber}
                    onItemSelect={(value) => setFilterValue('contractNumber', value)}
                    className="flex-1 h-12"
                    placeholder={t('contractNumber', { ns: 'contractsTable' })}
                    options={contracts?.map((contract) => ({
                      value: contract.contractNumber,
                      label: contract.contractNumber,
                    }))}
                    leftSide={
                      <span className="w-6 h-6 text-brand-brown">
                        <Hash02 />
                      </span>
                    }
                  />
                  <Select
                    name="garbageId"
                    value={filters.garbageId}
                    onItemSelect={(value) => setFilterValue('garbageId', value)}
                    className="flex-1 h-12"
                    placeholder={t('garbageName', { ns: 'objectItemsTable' })}
                    options={filtersQuery.data?.garbage?.map((garbage) => ({
                      value: garbage.id.toString(),
                      label: garbage.name,
                    }))}
                    leftSide={
                      <span className="w-6 h-6 text-brand-brown">
                        <Trash04 />
                      </span>
                    }
                  />
                </div>
                <div className="flex gap-2 flex-1 flex-col">
                  <Select
                    name="itemTypeId"
                    value={filters.itemTypeId}
                    onItemSelect={(value) => setFilterValue('itemTypeId', value)}
                    className="flex-1 h-12"
                    placeholder={t('itemType', { ns: 'contractsTable' })}
                    options={filtersQuery.data?.itemType?.map((itemType) => ({
                      value: itemType.id.toString(),
                      label: itemType.name,
                    }))}
                    leftSide={
                      <span className="text-transparent stroke-brand-brown">
                        <IconContainer3 size="sm" />
                      </span>
                    }
                  />
                  <div className="flex flex-1 gap-2 sm:flex-row flex-col">
                    <Select
                      name="objectAddressId"
                      value={filters.objectAddressId}
                      onItemSelect={(value) => setFilterValue('objectAddressId', value)}
                      className="flex-1 h-12"
                      placeholder={t('objectAddress', { ns: 'serviceHistoryPage' })}
                      options={filtersQuery.data?.addresses?.map((address) => ({
                        value: address.id.toString(),
                        label: address.address,
                      }))}
                      leftSide={<Map01 className="w-6 h-6 text-brand-brown" />}
                    />
                    <button
                      onClick={clearAllFilters}
                      className={cn(
                        'h-12 rounded-[8px] text-center col-span-1 sm:w-[85px] w-full justify-self-end items-center justify-center',
                        hasFilters ? 'bg-brand-green text-gray-10' : 'sm:flex hidden bg-[#F5F5F5] text-[#B9B9B9]',
                      )}
                    >
                      {t('clearAll')}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex sm:gap-4 gap-2 sm:flex-row flex-col sm:items-center">
                <Checkbox
                  checked={filters.showEndedContainers === 'true'}
                  onChange={(event) => setFilterValue('showEndedContainers', String(event.target.checked))}
                >
                  {t('showEndedContainers')}
                </Checkbox>
                <Checkbox
                  checked={filters.showEndedObjects === 'true'}
                  onChange={(event) => setFilterValue('showEndedObjects', String(event.target.checked))}
                >
                  {t('showEndedObjects')}
                </Checkbox>
              </div>
            </div>
          </div>
        </div>
        {!isBelowMd && mapComponent}
      </div>
      <div className="flex flex-col items-end justify-between flex-1 pb-6 gap-7">
        {objectsQuery.isLoading || objectItemsQuery.isLoading ? (
          <Skeleton.Node className="rounded-[32px] w-full h-[104px]" active />
        ) : objectsData?.data?.length ? (
          <div className="flex flex-col gap-4 sm:gap-5 lg:gap-6 w-full">
            {objectsData?.data?.map((object) => (
              <div
                key={object.id}
                className="gap-2 cursor-pointer border-[#F0F0F0] grid grid-cols-6 bg-white p-3 border rounded-[32px] w-full"
              >
                <div
                  onClick={() => toggleExpandItem(object.id)}
                  className="grid grid-cols-6 col-span-6 sm:gap-6 gap-2 p-3 text-xs gap-y-8 lg:gap-5 sm:text-base"
                >
                  <div className="flex order-1 items-start col-span-6 gap-2 sm:col-span-3 md:col-span-2">
                    <button
                      className={cn('col-span-1 text-gray-7', {
                        'opacity-0 pointer-events-none': !getObjectItemsForObject(object.id)?.length,
                      })}
                    >
                      {expandedItemIds.includes(object.id) ? (
                        <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6" />
                      ) : (
                        <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                      )}
                    </button>
                    <div className="flex flex-col gap-3 sm:flex-row justify-items-start">
                      <p className="text-base font-medium text-gray-10 sm:text-lg">{object.objectAddress?.address || object.fullAddress}</p>
                    </div>
                  </div>
                  <div className="col-span-6 items-start md:col-span-1 text-base sm:col-span-1 order-2 flex">
                    <div
                      className={cn(
                        'rounded-[99px] h-8 flex items-center justify-center px-3',
                        object.isActive ? 'bg-[#E7FDDF] text-[#279643]' : 'bg-[#EFEFEF] text-[#595959]',
                      )}
                    >
                      {object.isActive
                        ? t('active', { ns: 'common' })
                        : dayjs(object.contract?.startDate).isAfter(dayjs())
                        ? t('inFuture', { ns: 'common' })
                        : t('ended', { ns: 'common' })}
                    </div>
                  </div>
                  <div className="flex flex-col order-3 md:items-start sm:items-end items-start col-span-3 gap-2 sm:col-span-2 md:col-span-1">
                    <span className="font-medium text-gray-10 text-base">{t('contractNumber')}</span>
                    <span className="font-normal text-gray-10 text-base">{object.contract?.contractNumber}</span>
                  </div>
                  <div className="flex flex-col text-base items-end order-4 col-span-3 gap-2 sm:items-start sm:col-span-3 md:col-span-1">
                    <span className="text-right font-medium text-gray-10 sm:text-left leading-[24px]">
                      {t('dischargeSchedules', { ns: 'contractsTable' })}
                    </span>
                    <div className="flex gap-2">
                      <DownloadDischargeSchedulesPDFButton objectIds={[object.id]} />
                      <DownloadSchedulesExcelButton objectIds={[object.id]} />
                    </div>
                  </div>
                  <div className="order-5 col-span-6 flex flex-col sm:items-end items-start justify-between gap-2 sm:col-span-3 md:col-span-1">
                    <span className="text-base text-right text-[#A7AD2A]">{object.id}</span>
                    <Button className="w-full sm:w-[210px]" onClick={() => handleExtraServicesButtonClick(object.id)} variant="primaryAlt">
                      {t('additionalServices', { ns: 'objectsTable' })}
                    </Button>
                  </div>
                </div>
                {expandedItemIds.includes(object.id) &&
                  getObjectItemsForObject(object.id)?.map((objectItem) => (
                    <div key={objectItem.id} className="flex flex-col col-span-6 gap-4 rounded-[20px] bg-brand-green-light">
                      <div className="items-center gap-3 grid grid-cols-6 col-span-6 px-3 py-4 rounded-[20px]">
                        <div className="gap-y-5 grid grid-cols-6 sm:gap-5 items-center col-span-6 pb-5 border-b border-b-[#DFDFDF]">
                          <div className="flex items-center order-1 col-span-4 gap-2 sm:col-span-3 md:col-span-2">
                            <img
                              src={`/garbage/${i18n.language}/${objectItem.garbage?.id}.svg`}
                              alt="icon"
                              className="object-contain w-6 sm:w-12"
                            />
                            <span className="font-medium text-gray-10">
                              {objectItem.garbage?.name} ({objectItem.garbage?.garbageSku})
                            </span>
                          </div>
                          <div className="md:col-span-3 text-base col-span-2 sm:justify-self-start justify-self-end sm:col-span-1 order-2">
                            <div
                              className={cn(
                                'rounded-[99px] h-8 flex items-center justify-center px-3',
                                objectItem.isActive ? 'bg-[#E7FDDF] text-[#279643]' : 'bg-[#EFEFEF] text-[#595959]',
                              )}
                            >
                              {objectItem.isActive
                                ? t('active', { ns: 'common' })
                                : objectItem?.startDate && dayjs(objectItem?.startDate).isAfter(dayjs())
                                ? t('inFuture', { ns: 'common' })
                                : t('ended', { ns: 'common' })}
                            </div>
                          </div>
                          <div className="flex flex-col items-start justify-between order-2 col-span-5 sm:items-end sm:col-span-2 md:col-span-1">
                            <span className="text-[#A7AD2A]">{objectItem.id}</span>
                            <span className="font-medium text-gray-10">{objectItem.itemType?.name}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-6 col-span-6 pt-4 gap-y-5 lg:gap-5">
                          <div className="flex flex-col items-start order-1 sm:col-span-2 col-span-3 gap-2 md:col-span-1">
                            <span className="font-medium text-gray-10 leading-[24px]">{t('frequency', { ns: 'objectItemsTable' })}</span>
                            <span className="font-normal text-gray-10">
                              {objectItem.isActive
                                ? parseDischargeFrequencyText(objectItem.frequencyText, i18n.language as Locale, t)
                                : '---'}
                            </span>
                          </div>
                          <div className="flex flex-col items-end order-2 col-span-3 gap-2 sm:items-start sm:col-span-1">
                            <span className="font-medium sm:text-left text-right text-gray-10 leading-[24px]">
                              {t('upcomingDischargeDate', { ns: 'objectItemsTable' })}
                            </span>
                            <span className="font-normal text-gray-10">
                              {objectItem.upcomingDischargeDate && objectItem.isActive
                                ? dayjs(objectItem.upcomingDischargeDate).format(DATE_FORMAT)
                                : '---'}
                            </span>
                          </div>
                          <div className="flex flex-col order-4 col-span-3 gap-2 sm:items-end md:items-start sm:col-span-3 md:col-span-2">
                            <span className="font-medium text-gray-10 leading-[24px]">{t('period', { ns: 'contractsTable' })}</span>
                            <span className="font-normal text-gray-10">
                              {objectItem.startDate ? dayjs(objectItem?.startDate).format(DATE_FORMAT) : '---'} -{' '}
                              {objectItem?.endDate ? dayjs(objectItem?.endDate).format(DATE_FORMAT) : '---'}
                            </span>
                          </div>
                          <div className="flex flex-col items-end order-5 col-span-3 sm:col-span-2 gap-2 sm:items-start md:col-span-1">
                            <span className="text-right font-medium text-gray-10 sm:text-left leading-[24px]">
                              {t('dischargeSchedules', { ns: 'contractsTable' })}
                            </span>
                            <div className="flex gap-2">
                              {objectItem.isActive ? (
                                <>
                                  <DownloadDischargeSchedulesPDFButton objectItemIds={[objectItem.id]} />
                                  <DownloadSchedulesExcelButton objectItemIds={[objectItem.id]} />
                                </>
                              ) : (
                                <>---</>
                              )}
                            </div>
                          </div>
                          <div className="order-5 col-span-6 flex justify-center sm:justify-end sm:col-span-4 md:col-span-1">
                            <Button
                              onClick={() =>
                                handleAdditionalDischargeButtonClick(objectItem.id, objectItem.garbageId, object.objectAddress?.id)
                              }
                              variant="primaryAlt"
                              disabled={object.contract?.contractDebt}
                              className="sm:w-auto w-full"
                            >
                              <Plus className="w-5 h-5" />
                              <span>{t('orderAdditionalDischarge', { ns: 'objectItemsTable' })}</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ))}
          </div>
        ) : (
          <NoData />
        )}

        <Pagination
          hideOnSinglePage
          current={page}
          pageSize={pageSize}
          total={objectsData?.meta?.totalItems}
          onChange={handlePaginationChange}
          showSizeChanger
          pageSizeOptions={['10', '20', '50', '100']}
        />
      </div>
      <ObjectServicesModal
        isModalOpen={isServicesModalOpen}
        handleCancel={handleCloseServicesModal}
        objectId={selectedObjectIdForServicesModal}
      />
      <ObjectItemDischargesModal
        objectItemId={objectItemDischargesModalProps?.objectItemId}
        garbageId={objectItemDischargesModalProps?.garbageId}
        objectAddressId={objectItemDischargesModalProps?.objectAddressId}
        isModalOpen={isAdditionalDischargeModalOpen}
        handleCancel={handleCloseAdditionalDischargesModal}
      />
    </div>
  );
};

export default DesktopPage;
